import {
    ReleaseNotesHeader,
    ComponentLinks,
    LinkRow
} from "./shared";
import {
    useDocsI18n
} from "../../i18n";
import type {
    DocsTranslate
} from "../../anatomy/types";
import type {
    DocsTocItem
} from "../../components/toc";
import {
    BulletList,
    Paragraph,
    SubTitle,
    Article,
    Callout,
    Section
} from "../../components/content";
import {
    RELEASE_NOTES_PATHS
} from "../../nav";

type ChangeGroup = {
    names: Array<string>;
    count: number;
    id: string;
};

const TODO_COUNT = 10;

const NEW_GROUPS: Array<{
    names: Array<string>;
    titleKey: string;
}> = [
    {
        titleKey: "whatsNewPage.newBasics",
        names: [
            "DirectionProvider",
            "Icon",
            "AnimatedIcon",
            "Kbd",
            "Skeleton",
            "AspectRatio",
            "Badge",
            "Label"
        ]
    },
    {
        titleKey: "whatsNewPage.newLayout",
        names: [
            "Card",
            "Item",
            "Empty",
            "ScrollArea",
            "Alert",
            "Collapsible",
            "Accordion",
            "Tabs",
            "Table",
            "Resizable",
            "Carousel"
        ]
    },
    {
        titleKey: "whatsNewPage.newInputs",
        names: [
            "Field",
            "RadioGroup",
            "Toggle",
            "ToggleGroup",
            "ButtonGroup",
            "Slider",
            "ColorField",
            "ColorPicker",
            "ColorArea",
            "ColorSlider",
            "ColorSwatch",
            "ColorSwatchPicker",
            "InputOTP",
            "InputGroup",
            "Combobox",
            "NativeSelect",
            "FileUpload"
        ]
    },
    {
        titleKey: "whatsNewPage.newOverlays",
        names: [
            "Popover",
            "HoverCard",
            "Sheet",
            "DropdownMenu",
            "ContextMenu",
            "Menubar",
            "Command"
        ]
    },
    {
        titleKey: "whatsNewPage.newNavigation",
        names: [
            "Breadcrumb",
            "Pagination",
            "NavigationMenu",
            "Sidebar",
            "TabBar"
        ]
    },
    {
        titleKey: "whatsNewPage.newFeedback",
        names: [
            "Progress",
            "Stepper",
            "Timeline",
            "TreeView",
            "SwipeableRow"
        ]
    },
    {
        titleKey: "whatsNewPage.newData",
        names: [
            "DataTable",
            "Chart",
            "BarChart",
            "LineChart",
            "AreaChart",
            "PieChart",
            "RadarChart",
            "RadialChart",
            "Sparkline",
            "StatTile"
        ]
    },
    {
        titleKey: "whatsNewPage.newChat",
        names: [
            "Marker",
            "Attachment",
            "Bubble",
            "Message",
            "MessageScroller",
            "Composer",
            "Trace",
            "Citation",
            "Questionnaire"
        ]
    }
];

const CHANGE_GROUPS: Array<ChangeGroup> = [
    {
        names: [
            "Button",
            "HighlightButton"
        ],
        id: "buttons",
        count: 6
    },
    {
        names: [
            "Dialog",
            "Modal",
            "BottomSheet"
        ],
        id: "dialogs",
        count: 6
    },
    {
        names: [
            "TextInput",
            "TextAreaInput",
            "NumericInput",
            "SelectBox"
        ],
        id: "fields",
        count: 6
    },
    {
        names: [
            "CheckBox",
            "RadioButton",
            "Switch",
            "Chip"
        ],
        id: "controls",
        count: 4
    },
    {
        names: [
            "Menu",
            "EmbeddedMenu",
            "Sidebar"
        ],
        id: "menus",
        count: 6
    },
    {
        id: "feedback",
        names: [
            "Loading",
            "Toast",
            "SnackBar",
            "Tooltip"
        ],
        count: 6
    },
    {
        names: [
            "StateCard",
            "RowCard",
            "Sticker",
            "NotificationIndicator"
        ],
        id: "replaced",
        count: 4
    },
    {
        names: [
            "Avatar",
            "AvatarGroup",
            "SiteLogo"
        ],
        id: "people",
        count: 4
    },
    {
        names: [
            "Separator",
            "Card",
            "MarkdownViewer",
            "MarkdownEditor",
            "CodeViewer",
            "CodeEditor"
        ],
        id: "content",
        count: 6
    },
    {
        names: [
            "DateSelector",
            "DateTimeSheet",
            "DateTimePicker"
        ],
        id: "dates",
        count: 2
    },
    {
        names: [
            "Header",
            "MainHeader",
            "PageContainer",
            "ThemeSwitcher"
        ],
        id: "frame",
        count: 3
    }
];

const TOPIC_SECTIONS: Array<{
    links: Array<{
        titleKey: string;
        href: string;
    }>;
    count: number;
    id: string;
}> = [
    {
        links: [
            {
                titleKey: "page.theming",
                href: "/theming"
            },
            {
                href: "/foundations/color",
                titleKey: "page.color"
            },
            {
                href: "/foundations/spacing",
                titleKey: "page.spacing"
            },
            {
                href: "/foundations/typography",
                titleKey: "page.typography"
            }
        ],
        id: "theme",
        count: 8
    },
    {
        links: [
            {
                href: "/foundations/spacing",
                titleKey: "page.spacing"
            },
            {
                titleKey: "page.direction",
                href: "/direction"
            }
        ],
        id: "access",
        count: 8
    },
    {
        links: [
            {
                href: "/foundations/motion",
                titleKey: "page.motion"
            }
        ],
        id: "motion",
        count: 3
    },
    {
        links: [
            {
                titleKey: "page.direction",
                href: "/direction"
            }
        ],
        id: "locales",
        count: 4
    }
];

const getItems = (t: DocsTranslate, prefix: string, count: number) => Array.from({
    length: count
}, (_, index) => t(`whatsNewPage.${prefix}${index + 1}`));

export const getWhatsNewToc = (t: DocsTranslate): Array<DocsTocItem> => [
    {
        title: t("whatsNewPage.upgradeTitle"),
        id: "upgrade"
    },
    {
        title: t("whatsNewPage.todoTitle"),
        id: "todo"
    },
    {
        title: t("whatsNewPage.newTitle"),
        id: "new"
    },
    {
        title: t("whatsNewPage.changedTitle"),
        id: "changed"
    },
    ...TOPIC_SECTIONS.map((section) => ({
        title: t(`whatsNewPage.${section.id}Title`),
        id: section.id
    }))
];

const WhatsNewPage = () => {
    const {
        t
    } = useDocsI18n();

    return <Article>
        <ReleaseNotesHeader
            lead={t("releaseNotes.whatsNewLead")}
            title={t("page.whatsNew")}
        />
        <Callout
            title={t("whatsNewPage.calloutTitle")}
        >
            {t("whatsNewPage.calloutText")}
        </Callout>
        <LinkRow
            items={[
                {
                    title: t("whatsNewPage.openDeprecated"),
                    href: RELEASE_NOTES_PATHS.deprecated
                }
            ]}
        />
        <Section
            title={t("whatsNewPage.upgradeTitle")}
            id="upgrade"
        >
            <Paragraph>
                {t("whatsNewPage.upgradeText1")}
            </Paragraph>
            <Paragraph>
                {t("whatsNewPage.upgradeText2")}
            </Paragraph>
        </Section>
        <Section
            title={t("whatsNewPage.todoTitle")}
            id="todo"
        >
            <Paragraph
                isMuted={true}
            >
                {t("whatsNewPage.todoText")}
            </Paragraph>
            <BulletList
                items={getItems(t, "todo", TODO_COUNT)}
                isOrdered={true}
            />
        </Section>
        <Section
            title={t("whatsNewPage.newTitle")}
            id="new"
        >
            <Paragraph
                isMuted={true}
            >
                {t("whatsNewPage.newText")}
            </Paragraph>
            {
                NEW_GROUPS.flatMap((group) => [
                    <SubTitle
                        key={`${group.titleKey}-title`}
                    >
                        {t(group.titleKey)}
                    </SubTitle>,
                    <ComponentLinks
                        key={`${group.titleKey}-links`}
                        names={group.names}
                    />
                ])
            }
        </Section>
        <Section
            title={t("whatsNewPage.changedTitle")}
            id="changed"
        >
            <Paragraph
                isMuted={true}
            >
                {t("whatsNewPage.changedText")}
            </Paragraph>
            {
                CHANGE_GROUPS.flatMap((group) => [
                    <SubTitle
                        key={`${group.id}-title`}
                    >
                        {t(`whatsNewPage.${group.id}Title`)}
                    </SubTitle>,
                    <ComponentLinks
                        key={`${group.id}-links`}
                        names={group.names}
                        size="small"
                    />,
                    <BulletList
                        items={getItems(t, group.id, group.count)}
                        key={`${group.id}-items`}
                    />
                ])
            }
        </Section>
        {
            TOPIC_SECTIONS.map((section) => <Section
                title={t(`whatsNewPage.${section.id}Title`)}
                key={section.id}
                id={section.id}
            >
                <BulletList
                    items={getItems(t, section.id, section.count)}
                />
                <LinkRow
                    items={section.links.map((link) => ({
                        title: t(link.titleKey),
                        href: link.href
                    }))}
                    size="small"
                />
            </Section>)
        }
        <Callout
            title={t("whatsNewPage.openTitle")}
        >
            {t("whatsNewPage.openText")}
        </Callout>
    </Article>;
};
export default WhatsNewPage;
